import { useAuth } from '@hooks/useAuth';
import { KeyboardArrowDownOutlined, LogoutOutlined } from '@mui/icons-material';
import { Box, Divider, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useAuthStore } from '@store/authStore';
import { useState } from 'react';

import { MENU_PAPER_SX } from './topbar.styles';

export const ProfileMenu = () => {
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  const displayName = user?.name ?? '';
  const role = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '';
  const initials = displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <Box
        onClick={(e) => setProfileAnchor(e.currentTarget)}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          px: '12px',
          py: '6px',
          bgcolor: theme.palette.grey[100],
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: '9999px',
          maxWidth: 182,
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.palette.grey.A100 },
        })}
      >
        {user?.avatarUrl ? (
          <Box
            component="img"
            src={user.avatarUrl}
            alt={displayName}
            sx={(theme) => ({ width: 32, height: 32, borderRadius: '50%', border: `1px solid ${theme.palette.grey[300]}`, objectFit: 'cover', flexShrink: 0 })}
          />
        ) : (
          <Box
            sx={(theme) => ({
              width: 32, height: 32, bgcolor: theme.palette.primary.main, borderRadius: '50%',
              border: `1px solid ${theme.palette.grey[300]}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            })}
          >
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'white' }}>
              {initials}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={(theme) => ({ fontSize: '12px', fontWeight: 600, color: theme.palette.grey[700], lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })}>
            {displayName}
          </Typography>
          <Typography sx={(theme) => ({ fontSize: '11px', fontWeight: 500, color: theme.palette.grey[500], lineHeight: 1, mt: '2px' })}>
            {role}
          </Typography>
        </Box>
        <KeyboardArrowDownOutlined sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[700], flexShrink: 0 })} />
      </Box>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        disableScrollLock
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { ...MENU_PAPER_SX, width: 220, maxWidth: 220 } } }}
      >
        <MenuItem disabled sx={{ py: '10px', opacity: '1 !important' }}>
          <Box sx={{ minWidth: 0, width: '100%', pointerEvents: 'auto' }}>
            <Tooltip title={displayName} placement="top" arrow disableInteractive>
              <Typography sx={(theme) => ({ fontSize: '13px', fontWeight: 600, color: theme.palette.grey[700], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
                {displayName}
              </Typography>
            </Tooltip>
            <Typography sx={(theme) => ({ fontSize: '12px', color: theme.palette.grey[400], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
              {user?.email}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { setProfileAnchor(null); logout(); }}
          sx={(theme) => ({ fontSize: '13px', color: theme.palette.status.error.text, display: 'flex', alignItems: 'center', gap: '8px' })}
        >
          <LogoutOutlined sx={{ fontSize: 16 }} />
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};
